import type {
  SelectionList,
  TreeItem,
  ValidValueTypes,
} from '@revelio/iso-utility';

import {
  isLimitedCompanySelectionList,
  prefetchLimitedCompanyFilterRcids,
} from '../reports/report-builder/02-entity-configuration/mw-reports/company-filter-membership';
import { updateReportEntitySelections } from '../reports/report-builder/02-entity-configuration/mw-reports/report-entity-selection.utils';
import type { IReportEntityMap } from '../reports/report-builder/02-entity-configuration/mw-reports/utils';
import type { ReportBuilderDeliverable } from '../reports/report-builder/report-builder.model';
import type {
  GlobalAssistantReportParamSuggestion,
  GlobalAssistantReportPrefillMetrics,
  GlobalAssistantReportPrefillWarning,
} from './global-ai-assistant-api';
import { getReportParameterMapping } from './global-ai-assistant-report-parameter-catalog';
import {
  getMwSelectionListId,
  isListLevelSelection,
  toMwListSelectionsFromResolved,
  toMwTreeSelectionsFromResolved,
} from './global-ai-assistant-report-resolved-selection';

const findMwColumnIdForParameter = (
  reportEntityMap: IReportEntityMap,
  reportType: string,
  parameterId: string
): { columnId: string; selectionLists: string[] } | null => {
  const mapping = getReportParameterMapping(reportType, parameterId);
  if (!mapping) {
    return null;
  }

  const columns = reportEntityMap[reportType]?.columns ?? [];
  const column = columns.find((entry) =>
    mapping.mwColumnLabels.some((label) => entry.label === label)
  );
  if (!column?.id) {
    return null;
  }

  return {
    columnId: column.id,
    selectionLists:
      column.reportEntityOptions.selectionList.filters.map(String),
  };
};

const toMwSelectionsFromResolved = (
  resolved: NonNullable<
    GlobalAssistantReportParamSuggestion['resolvedSelection']
  >,
  selectionLists: string[],
  customSkillLists: SelectionList<ValidValueTypes>[] | null = null
): Record<string, TreeItem<string>> => {
  const selectionListId = getMwSelectionListId(
    selectionLists,
    resolved.levelId
  );
  if (isListLevelSelection(resolved.levelId)) {
    return toMwListSelectionsFromResolved(resolved, selectionListId);
  }
  return toMwTreeSelectionsFromResolved(
    resolved,
    selectionListId,
    selectionLists,
    customSkillLists
  );
};

const resolveLevelMismatchWarning = (params: {
  suggestion: GlobalAssistantReportParamSuggestion;
  selectionLists: string[];
}): GlobalAssistantReportPrefillWarning | null => {
  const resolved = params.suggestion.resolvedSelection;
  if (!resolved) {
    return null;
  }

  const roleSelectionLists = params.selectionLists.filter((selectionListId) =>
    selectionListId.startsWith('role_')
  );
  if (
    resolved.levelId.startsWith('role_') &&
    roleSelectionLists.length > 0 &&
    !roleSelectionLists.includes(resolved.levelId)
  ) {
    return {
      parameterId: params.suggestion.parameterId,
      parameterDisplayName: params.suggestion.displayName,
      selectionName: resolved.name,
      foundLevelId: resolved.levelId,
      supportedLevelIds: roleSelectionLists,
      reason: 'level_id_mismatch',
    };
  }

  const skillSelectionLists = params.selectionLists.filter((selectionListId) =>
    selectionListId.startsWith('skill')
  );
  if (
    resolved.levelId.startsWith('skill') &&
    skillSelectionLists.length > 0 &&
    !skillSelectionLists.includes(resolved.levelId)
  ) {
    return {
      parameterId: params.suggestion.parameterId,
      parameterDisplayName: params.suggestion.displayName,
      selectionName: resolved.name,
      foundLevelId: resolved.levelId,
      supportedLevelIds: skillSelectionLists,
      reason: 'level_id_mismatch',
    };
  }

  return null;
};

const resolveUnsupportedCompanyWarning = (params: {
  suggestion: GlobalAssistantReportParamSuggestion;
  selectionListId: string;
}): GlobalAssistantReportPrefillWarning => ({
  parameterId: params.suggestion.parameterId,
  parameterDisplayName: params.suggestion.displayName,
  selectionName: params.suggestion.resolvedSelection?.name ?? '',
  selectionListId: params.selectionListId,
  reason: 'company_not_in_list',
});

const collectLimitedCompanyListIdsForReport = (
  reportEntityMap: IReportEntityMap,
  reportType: string
): string[] => {
  const columns = reportEntityMap[reportType]?.columns ?? [];
  return columns.flatMap((column) =>
    column.reportEntityOptions.selectionList.filters
      .map(String)
      .filter(isLimitedCompanySelectionList)
  );
};

export async function applyGlobalAssistantReportPrefill(params: {
  reportId: ReportBuilderDeliverable['id'];
  reportType: string;
  suggestions: GlobalAssistantReportParamSuggestion[];
  reportEntityMap: IReportEntityMap;
  customSkillLists?: SelectionList<ValidValueTypes>[] | null;
}): Promise<GlobalAssistantReportPrefillMetrics> {
  const selectionsByColumn = new Map<
    string,
    Record<string, TreeItem<string>>
  >();
  const warningsByKey = new Map<string, GlobalAssistantReportPrefillWarning>();
  let appliedCount = 0;

  const limitedCompanyMemberSets = await prefetchLimitedCompanyFilterRcids(
    collectLimitedCompanyListIdsForReport(
      params.reportEntityMap,
      params.reportType
    )
  );

  params.suggestions.forEach((suggestion) => {
    if (!suggestion.resolvedSelection) {
      return;
    }

    const columnMatch = findMwColumnIdForParameter(
      params.reportEntityMap,
      params.reportType,
      suggestion.parameterId
    );
    if (!columnMatch) {
      return;
    }

    const resolved = suggestion.resolvedSelection;
    const selectionListId = getMwSelectionListId(
      columnMatch.selectionLists,
      resolved.levelId
    );
    if (
      isLimitedCompanySelectionList(selectionListId) &&
      resolved.levelId === 'company' &&
      resolved.rcid !== undefined
    ) {
      const memberSet = limitedCompanyMemberSets.get(selectionListId);
      if (memberSet instanceof Set && !memberSet.has(resolved.rcid)) {
        const warning = resolveUnsupportedCompanyWarning({
          suggestion,
          selectionListId,
        });
        warningsByKey.set(
          `${warning.parameterId}:${resolved.rcid}:${warning.selectionName}`,
          warning
        );
        return;
      }
    }

    const resolvedSelections = toMwSelectionsFromResolved(
      suggestion.resolvedSelection,
      columnMatch.selectionLists,
      params.customSkillLists ?? null
    );
    if (Object.keys(resolvedSelections).length === 0) {
      const warning = resolveLevelMismatchWarning({
        suggestion,
        selectionLists: columnMatch.selectionLists,
      });
      if (warning) {
        warningsByKey.set(
          `${warning.parameterId}:${warning.foundLevelId ?? 'unknown'}:${warning.selectionName}`,
          warning
        );
      }
      return;
    }

    appliedCount += 1;
    const existing = selectionsByColumn.get(columnMatch.columnId) ?? {};
    selectionsByColumn.set(columnMatch.columnId, {
      ...existing,
      ...resolvedSelections,
    });
  });

  selectionsByColumn.forEach((selections, columnId) => {
    updateReportEntitySelections({
      reportId: params.reportId,
      reportEntity: columnId,
      selections,
    });
  });

  const resolvedCount = params.suggestions.filter(
    (suggestion) => suggestion.resolutionStatus === 'resolved'
  ).length;

  return {
    extractedCount: params.suggestions.length,
    resolvedCount,
    appliedCount,
    skippedCount: params.suggestions.length - appliedCount,
    warnings: Array.from(warningsByKey.values()),
  };
}
